import { assertIntegerCents, type MoneyCents } from './money'
import type { VloerkleedCartItem, PricingBootstrap } from './types'
import { isCountBasedSku } from '../offerte-products'

// ============================================================================
// TYPES
// ============================================================================

export interface PriceLine {
  key: string
  label: string
  amountCents: MoneyCents
}

export interface VloerkleedItemPriceResult {
  itemId: string
  areaM2: number
  areaM2Rounded: number
  baseCents: MoneyCents
  packageCents: MoneyCents
  extrasCents: MoneyCents
  surchargeCents: MoneyCents
  totalCents: MoneyCents
  lines: PriceLine[]
}

export interface VloerkleedTotalResult {
  items: VloerkleedItemPriceResult[]
  subtotalCents: MoneyCents
  discountCents: MoneyCents
  discountLabel: string | null
  surchargeCents: MoneyCents
  btwCents: MoneyCents
  totalCents: MoneyCents
  minimumOrderMet: boolean
  minimumOrderAmountCents: MoneyCents
}

// ============================================================================
// PACKAGE INCLUDED EXTRAS — which extras are included in which package
// ============================================================================

const PACKAGE_INCLUDED_EXTRAS: Record<string, string[]> = {
  PROTECT: ['VEZELBESCHERMER'],
  PET: ['VEZELBESCHERMER', 'GEUR'],
  PREMIUM: ['VEZELBESCHERMER', 'KWETSBARE', 'ALLERGIE'],
}

export function getPackageIncludedExtras(packageSku: string | null): string[] {
  if (!packageSku) return []
  for (const [key, extras] of Object.entries(PACKAGE_INCLUDED_EXTRAS)) {
    if (packageSku.toUpperCase().includes(key)) return extras
  }
  return []
}

export function isExtraIncludedInPackage(extraSku: string, packageSku: string | null, extraName?: string): boolean {
  const included = getPackageIncludedExtras(packageSku)
  const skuUpper = extraSku.toUpperCase()
  const nameUpper = extraName?.toUpperCase() ?? ''
  return included.some((inc) => skuUpper.includes(inc) || nameUpper.includes(inc))
}

// ============================================================================
// AREA CALCULATION
// ============================================================================

export function calculateArea(item: VloerkleedCartItem): { areaM2: number; areaM2Rounded: number } {
  let areaM2 = 0

  switch (item.shape) {
    case 'rechthoekig': {
      if (item.lengthCm > 0 && item.widthCm > 0) {
        const areaCm2 = item.lengthCm * item.widthCm
        areaM2 = areaCm2 / 10000
      }
      break
    }
    case 'rond': {
      if (item.diameterCm > 0) {
        const radiusCm = item.diameterCm / 2
        areaM2 = (Math.PI * radiusCm * radiusCm) / 10000
      }
      break
    }
    case 'overig': {
      areaM2 = item.areaM2Manual
      break
    }
  }

  // Round to 0.01 m² using integer arithmetic (deterministic)
  const areaCentiM2 = Math.round(areaM2 * 100)
  const areaM2Rounded = areaCentiM2 / 100

  return { areaM2, areaM2Rounded }
}

// ============================================================================
// ITEM PRICE
// ============================================================================

export function calculateVloerkleedItemPrice(
  item: VloerkleedCartItem,
  bootstrap: PricingBootstrap,
  selectedProductSku?: string | null,
): VloerkleedItemPriceResult {
  const regioFactor = 1 + (bootstrap.regioToeslagPercentage ?? 0) / 100

  // Resolve primary product per-item: item.productCode is leading; fallback to
  // panel-wide selectedProductSku, dan eerste primary uit bootstrap.
  const itemSku = item.productCode ?? selectedProductSku ?? null
  const primaryProduct = (itemSku
    ? bootstrap.availableProducts?.find((p) => p.sku === itemSku)
    : null
  ) ?? bootstrap.availableProducts?.find((p) => !p.isExtra)
  const basePricePerM2 = primaryProduct?.basePrice ?? 0

  // Count-based pad: prijs = basePrice × aantal × regioFactor + extras × aantal.
  // Geen area of pakket-multiplier; extras (bijv. impregneren) wel meegenomen.
  if (isCountBasedSku(itemSku)) {
    const aantal = item.aantal ?? 0
    const baseEuros = basePricePerM2 * aantal * regioFactor
    const baseCents = Math.round(baseEuros * 100)
    assertIntegerCents(baseCents)

    const lines: PriceLine[] = []
    let extrasCents = 0

    for (const extraSku of item.extras) {
      const extra = bootstrap.availableProducts?.find((p) => p.sku === extraSku)
      if (!extra) continue

      let extraCents = 0
      if (extra.extraType === 'percentage' && extra.extraPercentage != null) {
        // Percentage van base (incl. aantal)
        extraCents = Math.round(baseEuros * (extra.extraPercentage / 100) * 100)
      } else {
        // Flat fee per stuk × aantal × regioFactor
        extraCents = Math.round(extra.basePrice * aantal * regioFactor * 100)
      }
      assertIntegerCents(extraCents)
      extrasCents += extraCents

      lines.push({
        key: `extra:${extra.sku}`,
        label: extra.name,
        amountCents: extraCents,
      })
    }

    const totalCents = baseCents + extrasCents
    return {
      itemId: item.id,
      areaM2: 0,
      areaM2Rounded: 0,
      baseCents,
      packageCents: baseCents,
      extrasCents,
      surchargeCents: 0,
      totalCents,
      lines,
    }
  }

  const { areaM2, areaM2Rounded } = calculateArea(item)
  const lines: PriceLine[] = []

  // Find selected package
  const pkg = item.selectedPackage
    ? bootstrap.packages.find((p) => p.sku === item.selectedPackage)
    : null

  // Package multiplier (percentage on top of base)
  const packageMultiplier =
    pkg && pkg.percentageMultiplier > 0 ? 1 + pkg.percentageMultiplier / 100 : 1

  // Base price WITHOUT package multiplier (for minimum enforcement)
  const baseOnlyEuros = areaM2Rounded * basePricePerM2 * regioFactor
  const baseOnlyCents = Math.round(baseOnlyEuros * 100)
  assertIntegerCents(baseOnlyCents)

  // Full price with package multiplier
  const basePriceEuros = areaM2Rounded * basePricePerM2 * packageMultiplier * regioFactor
  const packageCents = Math.round(basePriceEuros * 100)
  assertIntegerCents(packageCents)

  if (pkg) {
    lines.push({
      key: `pack:${pkg.sku}`,
      label: pkg.name,
      amountCents: packageCents,
    })
  }

  // Extras
  let extrasCents = 0
  const basePriceForExtrasEuros = areaM2Rounded * basePricePerM2 * regioFactor

  for (const extraSku of item.extras) {
    if (isExtraIncludedInPackage(extraSku, item.selectedPackage)) continue

    const extra = bootstrap.availableProducts?.find((p) => p.sku === extraSku)
    if (!extra) continue

    let extraCents = 0
    if (extra.extraType === 'percentage' && extra.extraPercentage != null) {
      // Percentage of base price (without package multiplier)
      extraCents = Math.round(basePriceForExtrasEuros * (extra.extraPercentage / 100) * 100)
    } else {
      // Flat fee with regio factor
      extraCents = Math.round(extra.basePrice * regioFactor * 100)
    }
    assertIntegerCents(extraCents)
    extrasCents += extraCents

    lines.push({
      key: `extra:${extra.sku}`,
      label: extra.name,
      amountCents: extraCents,
    })
  }

  const subtotalCents = packageCents + extrasCents
  const surchargeCents = 0 // Already factored into regioFactor

  return {
    itemId: item.id,
    areaM2,
    areaM2Rounded,
    baseCents: baseOnlyCents,
    packageCents,
    extrasCents,
    surchargeCents,
    totalCents: subtotalCents + surchargeCents,
    lines,
  }
}

// ============================================================================
// TOTAL PRICE (all items + minimum order + bundle discount)
// ============================================================================

export function calculateVloerkleedTotalPrice(
  items: VloerkleedCartItem[],
  bootstrap: PricingBootstrap,
  selectedProductSku?: string | null,
): VloerkleedTotalResult {
  const emptyResult: VloerkleedTotalResult = {
    items: [],
    subtotalCents: 0,
    discountCents: 0,
    discountLabel: null,
    surchargeCents: 0,
    btwCents: 0,
    totalCents: 0,
    minimumOrderMet: true,
    minimumOrderAmountCents: 0,
  }

  // Price items met geldige input: shape met area > 0 OF count met aantal > 0
  const validItems = items.filter((item) => {
    if (isCountBasedSku(item.productCode)) return (item.aantal ?? 0) > 0
    const { areaM2Rounded } = calculateArea(item)
    return areaM2Rounded > 0
  })

  if (validItems.length === 0) return emptyResult

  // Calculate per-item prices
  const itemResults = validItems.map((item) => calculateVloerkleedItemPrice(item, bootstrap, selectedProductSku))

  // Subtotal before discount
  const subtotalCents = itemResults.reduce((sum, r) => sum + r.totalCents, 0)

  // Bundle discount
  let discountCents = 0
  let discountLabel: string | null = null
  const bundleDiscounts = bootstrap.bundleDiscounts ?? []

  if (bundleDiscounts.length > 0 && itemResults.length > 0) {
    const applicableDiscount = bundleDiscounts
      .filter((bd) => {
        const meetingCount = itemResults.filter(
          (r) => r.areaM2Rounded >= (bd.minAreaM2 ?? 0),
        ).length
        return meetingCount >= bd.minItems
      })
      .sort((a, b) => b.discountPercentage - a.discountPercentage)[0]

    if (applicableDiscount && applicableDiscount.discountPercentage > 0) {
      const pct = applicableDiscount.discountPercentage
      discountCents = Math.round(subtotalCents * pct)
      const displayPct = Math.round(pct * 100)
      discountLabel = applicableDiscount.label || `Bundelkorting -${displayPct}%`
    }
  }

  // Minimum order enforcement
  const minimumOrderAmountCents = Math.round((bootstrap.minimumOrderExBtw ?? 0) * 100)
  const totalBaseCents = itemResults.reduce((sum, r) => sum + r.baseCents, 0)
  const totalPackageSurchargeCents = itemResults.reduce((sum, r) => sum + (r.packageCents - r.baseCents), 0)
  const totalExtrasCents = itemResults.reduce((sum, r) => sum + r.extrasCents, 0)

  let finalSubtotalCents = subtotalCents - discountCents

  if (minimumOrderAmountCents > 0 && totalBaseCents < minimumOrderAmountCents && itemResults.length > 0) {
    // Uplift base to minimum, keep package surcharge and extras on original area price
    const upliftedBaseCents = minimumOrderAmountCents
    finalSubtotalCents = upliftedBaseCents + totalPackageSurchargeCents + totalExtrasCents - discountCents

    // Distribute base uplift proportionally across items for display
    const totalArea = itemResults.reduce((sum, r) => sum + r.areaM2Rounded, 0)
    for (const result of itemResults) {
      const areaRatio = totalArea > 0 ? result.areaM2Rounded / totalArea : 1 / itemResults.length
      const itemBaseUplift = Math.round((upliftedBaseCents - totalBaseCents) * areaRatio)
      result.totalCents += itemBaseUplift
    }
  }

  // BTW (21%)
  const btwCents = Math.round(finalSubtotalCents * 0.21)

  return {
    items: itemResults,
    subtotalCents: finalSubtotalCents,
    discountCents,
    discountLabel,
    surchargeCents: 0,
    btwCents,
    totalCents: finalSubtotalCents + btwCents,
    minimumOrderMet: finalSubtotalCents >= minimumOrderAmountCents,
    minimumOrderAmountCents,
  }
}
